
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Save, X, Trash2, Plus, Package } from "lucide-react";
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

export default function EditInventoryMovementModal({
    isOpen,
    onClose,
    onSave,
    movement,
    warehouses,
    initialItems,
    books,
    items: availableItems
}) {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        document_number: '',
        document_date: '',
        type: '',
        from_warehouse_id: '',
        to_warehouse_id: '',
        notes: ''
    });
    const [items, setItems] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (movement && isOpen) {
            setFormData({
                ...movement,
                document_date: movement.document_date ? format(new Date(movement.document_date), 'yyyy-MM-dd') : ''
            });
            setItems(initialItems || []);
        }
    }, [movement, isOpen, initialItems]);

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [field]: value };
        setItems(newItems);
    };

    const addItem = () => {
        setItems([...items, { item_id: '', quantity: 1, movement_id: movement.id }]);
    };

    const removeItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const getItemDisplayName = (itemId) => {
        const item = availableItems.find(i => i.id === itemId);
        if (!item) return t('inventory_movement.select_item');
        
        let displayName = item.name;
        
        // Добавляем специфичную информацию в зависимости от типа
        if (item.type === 'book_edition' && item.metadata?.isbn) {
            displayName += ` (ISBN: ${item.metadata.isbn})`;
        } else if (item.type === 'simple_product' && item.metadata?.gtin) {
            displayName += ` (GTIN: ${item.metadata.gtin})`;
        } else if (item.type === 'bundle') {
            displayName += ` (Bundle)`;
        }
        
        return displayName;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (items.some(item => !item.item_id || !item.quantity)) {
            alert(t('inventory.fill_all_fields'));
            return;
        }
        setIsSubmitting(true);
        try {
            await onSave(formData, items);
        } catch (error) {
            console.error("Error saving movement:", error);
        }
        setIsSubmitting(false);
    };

    if (!isOpen) return null;
    
    const isTransferOrShipment = formData.type === 'transfer' || formData.type === 'shipment' || formData.type === 'write_off';
    const isReceiptOrTransfer = formData.type === 'receipt' || formData.type === 'transfer';

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-slate-900">
                        {t('inventory.edit_movement_title', { number: movement.document_number })}
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6 pt-4">
                    <Card className="border-slate-200">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-lg">{t('inventory.basic_info')}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="document_number">{t('inventory.document_number')}</Label>
                                    <Input id="document_number" value={formData.document_number} onChange={e => handleFieldChange('document_number', e.target.value)} required />
                                </div>
                                <div>
                                    <Label htmlFor="document_date">{t('inventory.document_date')}</Label>
                                    <Input id="document_date" type="date" value={formData.document_date} onChange={e => handleFieldChange('document_date', e.target.value)} required />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {isTransferOrShipment && (
                                    <div>
                                        <Label htmlFor="from_warehouse">Склад отправитель</Label>
                                        <Select value={formData.from_warehouse_id || ''} onValueChange={(v) => handleFieldChange('from_warehouse_id', v)}>
                                            <SelectTrigger><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                {warehouses.filter(w => w.is_active).map(w => 
                                                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                {(isReceiptOrTransfer || formData.document_type === 'receipt') && (
                                    <div>
                                        <Label htmlFor="to_warehouse">Склад получатель</Label>
                                        <Select value={formData.warehouse_id || formData.to_warehouse_id || ''} onValueChange={(v) => handleFieldChange('warehouse_id', v)}>
                                            <SelectTrigger><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                {warehouses.filter(w => w.is_active).map(w => 
                                                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div>
                                    <Label htmlFor="status">Статус накладной</Label>
                                    <Select value={formData.status} onValueChange={(v) => handleFieldChange('status', v)}>
                                        <SelectTrigger><SelectValue/></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="draft">Черновик</SelectItem>
                                            <SelectItem value="executed">Проведен</SelectItem>
                                            <SelectItem value="payment_pending">Ожидает оплаты</SelectItem>
                                            <SelectItem value="payment_complete">Оплата завершена</SelectItem>
                                            <SelectItem value="payment_expired">Оплата просрочена</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            <div>
                                <Label htmlFor="basis">Основание</Label>
                                <Input id="basis" value={formData.basis || ''} onChange={e => handleFieldChange('basis', e.target.value)} />
                            </div>

                            <div>
                                <Label htmlFor="notes">Примечания</Label>
                                <Textarea id="notes" value={formData.notes || ''} onChange={e => handleFieldChange('notes', e.target.value)} />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200">
                        <CardHeader className="pb-4">
                          <CardTitle className="text-lg flex items-center justify-between">
                            <span>Позиции накладной</span>
                            <Button type="button" onClick={addItem} variant="outline" size="sm">
                              <Plus className="w-4 h-4 mr-2" />
                              Добавить позицию
                            </Button>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {items.map((item, index) => (
                            <div key={item.id || index} className="grid grid-cols-12 gap-4 items-end p-4 border border-slate-200 rounded-lg">
                              <div className="col-span-8">
                                <Label>Товар *</Label>
                                <Select 
                                  value={item.item_id} 
                                  onValueChange={(value) => handleItemChange(index, 'item_id', value)}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Выберите товар" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableItems.map((itemOption) => (
                                      <SelectItem key={itemOption.id} value={itemOption.id}>
                                        {getItemDisplayName(itemOption.id)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div className="col-span-3">
                                <Label>Количество *</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                                />
                              </div>
                              
                              <div className="col-span-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeItem(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </CardContent>
                    </Card>

                    <DialogFooter className="pt-6 gap-2">
                        <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                            <X className="w-4 h-4 mr-2" /> Отмена
                        </Button>
                        <Button type="submit" disabled={isSubmitting} className="bg-amber-600 hover:bg-amber-700">
                            <Save className="w-4 h-4 mr-2" /> {isSubmitting ? 'Сохранение...' : 'Сохранить'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
